import { ApiInstance, RetrySettings } from '@src/services/api-instance';
import { getAuthToken } from '@src/services/auth-service';
import { RequestGetByIdResponseDTO } from './dto/requests.getById.response.dto';
import { SearchRunsResponseDTO } from './dto/requests.search.response.dto';
import { AggregateRunsResponseDTO } from './dto/requests.aggregate.response.dto';
import { OverviewResponseDTO } from './dto/requests.overview.response.dto';

export type SearchRunsParams = {
  organizationId: string;
  since: string;
  until?: string;
  flowName?: string;
  canonicalFlowKey?: string;
  status?: 'succeeded' | 'failed' | 'running' | 'all';
  contextSearch?: string;
  limit?: number;
  offset?: number;
  sortBy?: 'createdAt' | 'flowName' | 'status';
  sortDir?: 'asc' | 'desc';
};

export type AggregateRunsParams = {
  organizationId: string;
  since: string;
  until?: string;
  groupBy?: 'flow_name' | 'canonical_flow_key' | 'both';
};

export type OverviewParams = {
  since: string;
  until?: string;
};

export class RequestsService {
  private readonly client: ApiInstance;

  constructor(client: ApiInstance) {
    this.client = new ApiInstance(`${client.getBaseUrl()}/requests`, client.getTimeout());
  }

  async getById(request: { id: string; settings?: RetrySettings }): Promise<RequestGetByIdResponseDTO> {
    return (
      await this.client.get<RequestGetByIdResponseDTO>(
        `${request.id}`,
        {},
        {
          bearerToken: await getAuthToken(),
          settings: request.settings,
        },
      )
    ).data;
  }

  async search(request: { params: SearchRunsParams; settings?: RetrySettings }): Promise<SearchRunsResponseDTO> {
    return (
      await this.client.get<SearchRunsResponseDTO>('search', request.params, {
        bearerToken: await getAuthToken(),
        settings: request.settings,
      })
    ).data;
  }

  async aggregate(request: {
    params: AggregateRunsParams;
    settings?: RetrySettings;
  }): Promise<AggregateRunsResponseDTO> {
    return (
      await this.client.get<AggregateRunsResponseDTO>('aggregate', request.params, {
        bearerToken: await getAuthToken(),
        settings: request.settings,
      })
    ).data;
  }

  async overview(request: { params: OverviewParams; settings?: RetrySettings }): Promise<OverviewResponseDTO> {
    return (
      await this.client.get<OverviewResponseDTO>('overview', request.params, {
        bearerToken: await getAuthToken(),
        settings: request.settings,
      })
    ).data;
  }
}
